import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  AlertTriangle,
  ArrowLeft,
  Bot,
  Loader2,
  Phone,
  Rocket,
  ShieldAlert,
  Upload,
  X,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useProfile } from '@/api/profile'
import { useAIAgents } from '@/api/aiAgents'
import { useCompany, useCompanyPhoneNumbers } from '@/api/company'
import {
  useCreateBatchCall,
  useUpdateBatchCall,
  type BatchCall,
} from '@/api/batchCalls'

// =============================================================================
// Schéma RHF / Zod
// =============================================================================
const schema = z.object({
  name: z.string().trim().min(1, 'name_required'),
  ai_agent: z.string().min(1, 'agent_required'),
  phone_number: z.string().min(1, 'number_required'),
  phone_numbers: z.array(z.string()).min(1, 'phones_required'),
  context_message: z.string(),
  timing: z.enum(['immediate', 'scheduled']),
  scheduled_at: z.string(),
  retry_enabled: z.boolean(),
  retry_at: z.string(),
  call_delay: z.number().int().min(0).max(600),
  skip_if_no_appointment: z.boolean(),
  no_answer_action: z.enum(['none', 'sms_client']),
  no_answer_sms_template: z.string(),
})
type FormValues = z.infer<typeof schema>

const DEFAULTS: FormValues = {
  name: '',
  ai_agent: '',
  phone_number: '',
  phone_numbers: [],
  context_message: '',
  timing: 'scheduled',
  scheduled_at: '',
  retry_enabled: false,
  retry_at: '',
  call_delay: 15,
  skip_if_no_appointment: true,
  no_answer_action: 'none',
  no_answer_sms_template:
    'Bonjour, nous avons tenté de vous joindre. Veuillez nous rappeler au {company_phone}.',
}

// =============================================================================
// Helpers CSV / phone parsing
// =============================================================================
function normalizePhone(raw: string): string | null {
  const cleaned = raw.replace(/["'\s\-(). ]/g, '')
  if (/^\+[1-9]\d{9,14}$/.test(cleaned)) return cleaned
  if (/^1\d{10}$/.test(cleaned)) return `+${cleaned}`
  if (/^\d{10}$/.test(cleaned)) return `+1${cleaned}`
  return null
}

function parseCSVLine(line: string): string[] {
  const out: string[] = []
  let current = ''
  let inQuotes = false
  for (const ch of line) {
    if (ch === '"') inQuotes = !inQuotes
    else if ((ch === ',' || ch === ';' || ch === '\t') && !inQuotes) {
      out.push(current.trim())
      current = ''
    } else current += ch
  }
  out.push(current.trim())
  return out
}

interface ParsedCsv {
  /** Tableau d'objets {phone_number, first_name, ...} si métadonnées, sinon objets {phone_number} seulement. */
  records: Array<Record<string, string>>
  /** Headers de métadonnées (hors la colonne phone). */
  metaHeaders: string[]
  invalidLines: number
  duplicates: number
}

const PHONE_HEADER_CANDIDATES = [
  'phone',
  'phone_number',
  'telephone',
  'téléphone',
  'numero',
  'numéro',
  'tel',
  'mobile',
  'cell',
  'number',
]

async function parseCsvFile(file: File): Promise<ParsedCsv> {
  let text = await file.text()
  // Retire le BOM UTF-8 si présent (Excel).
  if (text.charCodeAt(0) === 0xfeff) text = text.slice(1)
  const lines = text.split(/\r?\n|\r/).filter((l) => l.trim().length > 0)
  if (lines.length === 0) {
    return { records: [], metaHeaders: [], invalidLines: 0, duplicates: 0 }
  }

  const rawHeaders = parseCSVLine(lines[0]).map((h) =>
    h.toLowerCase().replace(/\s+/g, '_'),
  )

  // Tente détection colonne phone par nom de header.
  let phoneIdx = rawHeaders.findIndex((h) => PHONE_HEADER_CANDIDATES.includes(h))

  // Sinon, détection par contenu : scanne la première ligne data pour une colonne
  // dont la valeur normalise correctement en E.164.
  if (phoneIdx === -1 && lines.length > 1) {
    const firstDataRow = parseCSVLine(lines[1])
    phoneIdx = firstDataRow.findIndex((cell) => normalizePhone(cell) !== null)
  }

  if (phoneIdx === -1) {
    return { records: [], metaHeaders: [], invalidLines: 0, duplicates: 0 }
  }

  const metaHeaders = rawHeaders.filter((_, i) => i !== phoneIdx)
  const seen = new Set<string>()
  const records: Array<Record<string, string>> = []
  let invalid = 0
  let duplicates = 0

  for (let i = 1; i < lines.length; i++) {
    const cells = parseCSVLine(lines[i])
    const raw = cells[phoneIdx] ?? ''
    const normalized = normalizePhone(raw)
    if (!normalized) {
      invalid++
      continue
    }
    if (seen.has(normalized)) {
      duplicates++
      continue
    }
    seen.add(normalized)
    const record: Record<string, string> = { phone_number: normalized }
    rawHeaders.forEach((header, idx) => {
      if (idx !== phoneIdx && cells[idx]) {
        record[header] = cells[idx]
      }
    })
    records.push(record)
  }

  return { records, metaHeaders, invalidLines: invalid, duplicates }
}

// =============================================================================
// Composant
// =============================================================================
interface Props {
  initialData?: BatchCall | null
  onSuccess: () => void
  onCancel: () => void
  onNavigateToAIAgents?: () => void
}

export function BatchCallForm({
  initialData,
  onSuccess,
  onCancel,
  onNavigateToAIAgents,
}: Props) {
  const { t } = useTranslation()
  const { data: profile } = useProfile()
  const companyId = profile?.company

  const { data: company } = useCompany(companyId)
  const { data: agents = [], isLoading: loadingAgents } = useAIAgents(companyId)
  const { data: phoneData, isLoading: loadingPhones } =
    useCompanyPhoneNumbers(companyId)
  const phoneNumbers = phoneData?.phone_numbers ?? []

  const createBatch = useCreateBatchCall()
  const updateBatch = useUpdateBatchCall(initialData?.id ?? 0)

  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [parsingCsv, setParsingCsv] = useState(false)
  // Records enrichis du CSV (avec first_name, last_name, etc.). Si vide, le submit
  // envoie juste `phone_numbers: string[]`. Sinon envoie le format objet.
  const [enrichedRecords, setEnrichedRecords] = useState<
    Array<Record<string, string>>
  >([])

  const activeAgents = useMemo(
    () => agents.filter((a) => a.is_active),
    [agents],
  )

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: DEFAULTS,
  })
  const { control, register, setValue, handleSubmit, reset, formState, getValues } =
    form

  // Hydrate quand on édite un existant.
  useEffect(() => {
    if (initialData) {
      reset({
        name: initialData.name || '',
        ai_agent: initialData.ai_agent?.toString() || '',
        phone_number: initialData.phone_number?.toString() || '',
        phone_numbers: (initialData.phone_numbers || []).map((n) =>
          typeof n === 'string' ? n : (n.phone_number ?? ''),
        ),
        context_message: initialData.context_message || '',
        timing: initialData.timing || 'scheduled',
        scheduled_at: initialData.scheduled_at?.slice(0, 16) || '',
        retry_enabled: initialData.retry_enabled || false,
        retry_at: initialData.retry_at?.slice(0, 16) || '',
        call_delay: initialData.call_delay || 15,
        skip_if_no_appointment: initialData.skip_if_no_appointment ?? true,
        no_answer_action:
          (initialData.no_answer_action === 'sms_client'
            ? 'sms_client'
            : 'none') as FormValues['no_answer_action'],
        no_answer_sms_template:
          initialData.no_answer_sms_template ?? DEFAULTS.no_answer_sms_template,
      })
    }
  }, [initialData, reset])

  const timing = useWatch({ control, name: 'timing' })
  const retryEnabled = useWatch({ control, name: 'retry_enabled' })
  const noAnswerAction = useWatch({ control, name: 'no_answer_action' })
  const phones = useWatch({ control, name: 'phone_numbers' })

  const handleCsvUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setCsvFile(file)
    setParsingCsv(true)
    try {
      const result = await parseCsvFile(file)
      const phones = result.records.map((r) => r.phone_number)
      const merged = Array.from(
        new Set([...getValues('phone_numbers'), ...phones]),
      )
      setValue('phone_numbers', merged, {
        shouldDirty: true,
        shouldValidate: true,
      })
      // Si le CSV a des métadonnées, on les stocke pour les envoyer au backend.
      if (result.metaHeaders.length > 0) {
        setEnrichedRecords(result.records)
      } else {
        setEnrichedRecords([])
      }
      toast.success(
        t('manager.batchCalls.form.toast.csvImported', {
          imported: phones.length,
          invalid: result.invalidLines,
          duplicates: result.duplicates,
        }),
      )
    } catch {
      toast.error(t('manager.batchCalls.form.toast.csvError'))
    } finally {
      setParsingCsv(false)
    }
  }

  const removePhone = (phone: string) => {
    setValue(
      'phone_numbers',
      getValues('phone_numbers').filter((p) => p !== phone),
      { shouldDirty: true, shouldValidate: true },
    )
    // Garde aligné l'enrichedRecords.
    setEnrichedRecords((prev) => prev.filter((r) => r.phone_number !== phone))
  }

  // Timezone effectif : prioritairement celui de l'entreprise (backend l'attend),
  // fallback sur le navigateur. Évite les décalages quand un admin voyage.
  const effectiveTimezone = useMemo(
    () =>
      company?.timezone ||
      Intl.DateTimeFormat().resolvedOptions().timeZone ||
      'America/Montreal',
    [company?.timezone],
  )

  const onSubmit = async (values: FormValues) => {
    // ⚠️ Datetime : on envoie la valeur datetime-local BRUTE (ex: "2026-05-12T14:00")
    // SANS la convertir en UTC ici. Le backend strip le tzinfo, réapplique le
    // timezone entreprise et convertit en UTC lui-même (`validate()` du serializer
    // BatchCallSerializer). Faire .toISOString() côté client = double conversion.
    const scheduledAt =
      values.timing === 'scheduled' && values.scheduled_at
        ? values.scheduled_at
        : null
    const retryAt =
      values.retry_enabled && values.retry_at ? values.retry_at : null

    // Si l'utilisateur a importé un CSV avec métadonnées, on envoie le format
    // enrichi pour permettre la personnalisation des appels. Sinon, juste les strings.
    const phonesPayload: Array<string | Record<string, string>> =
      enrichedRecords.length > 0 &&
      enrichedRecords.every((r) => values.phone_numbers.includes(r.phone_number))
        ? enrichedRecords.filter((r) =>
            values.phone_numbers.includes(r.phone_number),
          )
        : values.phone_numbers

    const payload = {
      name: values.name,
      ai_agent: Number(values.ai_agent),
      phone_number: Number(values.phone_number),
      phone_numbers: phonesPayload,
      context_message: values.context_message,
      timing: values.timing,
      scheduled_at: scheduledAt,
      scheduled_at_timezone: scheduledAt ? effectiveTimezone : null,
      retry_enabled: values.retry_enabled,
      retry_at: retryAt,
      retry_at_timezone: retryAt ? effectiveTimezone : null,
      call_delay: values.call_delay,
      skip_if_no_appointment: values.skip_if_no_appointment,
      no_answer_action: values.no_answer_action,
      no_answer_sms_template: values.no_answer_sms_template,
    }

    try {
      if (initialData) {
        await updateBatch.mutateAsync(payload)
        toast.success(t('manager.batchCalls.form.toast.updated'))
      } else {
        const created = await createBatch.mutateAsync(payload)
        if (created.dnc_removed_count) {
          toast.warning(
            t('manager.batchCalls.form.toast.dncRemoved', {
              count: created.dnc_removed_count,
            }),
          )
        }
        toast.success(t('manager.batchCalls.form.toast.created'))
      }
      onSuccess()
    } catch {
      toast.error(t('manager.batchCalls.form.toast.saveError'))
    }
  }

  const isDataLoading = loadingAgents || loadingPhones
  const noAgents = !loadingAgents && activeAgents.length === 0
  const noNumbers = !loadingPhones && phoneNumbers.length === 0
  const isSubmitting =
    formState.isSubmitting || createBatch.isPending || updateBatch.isPending

  const errMsg = (key?: string) =>
    key ? t(`manager.batchCalls.errors.${key}`) : null

  if (isDataLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    )
  }

  if (noAgents || noNumbers) {
    return (
      <div className="rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-6 max-w-2xl">
        <div className="flex items-start gap-3">
          <ShieldAlert className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-amber-900 dark:text-amber-200">
              {noAgents
                ? t('manager.batchCalls.form.requirements.noAgentsTitle')
                : t('manager.batchCalls.form.requirements.noNumbersTitle')}
            </h3>
            <p className="text-sm text-amber-800 dark:text-amber-300 mt-1">
              {noAgents
                ? t('manager.batchCalls.form.requirements.noAgentsDesc')
                : t('manager.batchCalls.form.requirements.noNumbersDesc')}
            </p>
            <div className="flex gap-2 mt-4">
              <Button type="button" variant="outline" onClick={onCancel}>
                {t('manager.batchCalls.form.cancel')}
              </Button>
              {noAgents && onNavigateToAIAgents && (
                <Button
                  type="button"
                  onClick={onNavigateToAIAgents}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  <Bot className="h-4 w-4 mr-2" />
                  {t('manager.batchCalls.form.requirements.goToAgents')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('manager.batchCalls.form.back')}
        </Button>
        <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
          {initialData
            ? t('manager.batchCalls.form.editTitle')
            : t('manager.batchCalls.form.createTitle')}
        </h2>
      </div>

      {/* 1. Identité */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 space-y-4">
        <h3 className="font-semibold text-slate-900 dark:text-white">
          {t('manager.batchCalls.form.sections.identity')}
        </h3>

        <div>
          <Label htmlFor="batch-name">
            {t('manager.batchCalls.form.fields.name')}{' '}
            <span className="text-red-500">*</span>
          </Label>
          <Input
            id="batch-name"
            placeholder={t('manager.batchCalls.form.fields.namePlaceholder')}
            className="mt-1"
            {...register('name')}
          />
          {formState.errors.name && (
            <p className="text-xs text-red-500 mt-1">
              {errMsg(formState.errors.name.message)}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>
              {t('manager.batchCalls.form.fields.aiAgent')}{' '}
              <span className="text-red-500">*</span>
            </Label>
            <Controller
              control={control}
              name="ai_agent"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger className="mt-1">
                    <SelectValue
                      placeholder={t(
                        'manager.batchCalls.form.fields.aiAgentPlaceholder',
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {activeAgents.map((a) => (
                      <SelectItem key={a.id} value={String(a.id)}>
                        {a.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {formState.errors.ai_agent && (
              <p className="text-xs text-red-500 mt-1">
                {errMsg(formState.errors.ai_agent.message)}
              </p>
            )}
          </div>

          <div>
            <Label>
              {t('manager.batchCalls.form.fields.outboundNumber')}{' '}
              <span className="text-red-500">*</span>
            </Label>
            <Controller
              control={control}
              name="phone_number"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger className="mt-1">
                    <SelectValue
                      placeholder={t(
                        'manager.batchCalls.form.fields.outboundNumberPlaceholder',
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {phoneNumbers.map((n) => (
                      <SelectItem key={n.id} value={String(n.id)}>
                        {n.phone_number}
                        {n.label ? ` - ${n.label}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {formState.errors.phone_number && (
              <p className="text-xs text-red-500 mt-1">
                {errMsg(formState.errors.phone_number.message)}
              </p>
            )}
          </div>
        </div>

        <div>
          <Label htmlFor="batch-context">
            {t('manager.batchCalls.form.fields.contextMessage')}
          </Label>
          <Textarea
            id="batch-context"
            rows={3}
            placeholder={t(
              'manager.batchCalls.form.fields.contextMessagePlaceholder',
            )}
            className="mt-1"
            {...register('context_message')}
          />
        </div>
      </div>

      {/* 2. Numéros à appeler */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 space-y-4">
        <h3 className="font-semibold text-slate-900 dark:text-white">
          {t('manager.batchCalls.form.sections.targets')}
        </h3>

        <label
          htmlFor="csv-input"
          className="block border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg p-6 text-center hover:border-slate-400 dark:hover:border-slate-500 transition-colors cursor-pointer"
        >
          <input
            id="csv-input"
            type="file"
            accept=".csv,.txt"
            className="hidden"
            onChange={(e) => void handleCsvUpload(e)}
          />
          {parsingCsv ? (
            <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('manager.batchCalls.form.fields.csvParsing')}
            </div>
          ) : csvFile ? (
            <div>
              <p className="font-medium text-slate-900 dark:text-white">
                {csvFile.name}
              </p>
              <p className="text-sm text-slate-400 mt-0.5">
                {t('manager.batchCalls.form.fields.csvLoadAnother')}
              </p>
            </div>
          ) : (
            <>
              <Upload className="w-8 h-8 text-slate-300 dark:text-slate-600 mx-auto mb-2" />
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {t('manager.batchCalls.form.fields.csvDropzone')}
              </p>
              <p className="text-xs text-slate-400 mt-1">
                {t('manager.batchCalls.form.fields.csvHint')}
              </p>
            </>
          )}
        </label>

        {phones.length > 0 ? (
          <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/40 max-h-60 overflow-y-auto">
            <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 dark:border-slate-800 text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">
              <span>
                {t('manager.batchCalls.form.fields.numbersList', {
                  count: phones.length,
                })}
              </span>
              <button
                type="button"
                onClick={() =>
                  setValue('phone_numbers', [], { shouldDirty: true })
                }
                className="text-red-500 hover:text-red-600"
              >
                {t('manager.batchCalls.form.fields.clearAll')}
              </button>
            </div>
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {phones.map((phone) => (
                <li
                  key={phone}
                  className="flex items-center justify-between px-4 py-2"
                >
                  <span className="text-sm font-mono text-slate-700 dark:text-slate-300">
                    {phone}
                  </span>
                  <button
                    type="button"
                    onClick={() => removePhone(phone)}
                    className="text-slate-400 hover:text-red-500"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          formState.errors.phone_numbers && (
            <p className="text-xs text-red-500">
              {errMsg(formState.errors.phone_numbers.message)}
            </p>
          )
        )}
      </div>

      {/* 3. Planification */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 space-y-4">
        <h3 className="font-semibold text-slate-900 dark:text-white">
          {t('manager.batchCalls.form.sections.scheduling')}
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => setValue('timing', 'immediate', { shouldDirty: true })}
            className={`p-4 border-2 rounded-lg text-left transition-all ${
              timing === 'immediate'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10'
                : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Rocket className="h-4 w-4 text-blue-600" />
              <span className="font-medium text-sm text-slate-900 dark:text-white">
                {t('manager.batchCalls.form.timing.immediate')}
              </span>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t('manager.batchCalls.form.timing.immediateDesc')}
            </p>
          </button>
          <button
            type="button"
            onClick={() => setValue('timing', 'scheduled', { shouldDirty: true })}
            className={`p-4 border-2 rounded-lg text-left transition-all ${
              timing === 'scheduled'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10'
                : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Phone className="h-4 w-4 text-blue-600" />
              <span className="font-medium text-sm text-slate-900 dark:text-white">
                {t('manager.batchCalls.form.timing.scheduled')}
              </span>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t('manager.batchCalls.form.timing.scheduledDesc')}
            </p>
          </button>
        </div>

        {timing === 'scheduled' && (
          <div>
            <Label htmlFor="scheduled-at">
              {t('manager.batchCalls.form.fields.scheduledAt')}
            </Label>
            <Input
              id="scheduled-at"
              type="datetime-local"
              className="mt-1"
              {...register('scheduled_at')}
            />
            <p className="text-xs text-slate-400 mt-1">
              {t('manager.batchCalls.form.fields.timezoneHint', {
                tz: effectiveTimezone,
              })}
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="call-delay">
              {t('manager.batchCalls.form.fields.callDelay')}
            </Label>
            <Input
              id="call-delay"
              type="number"
              min={0}
              max={600}
              className="mt-1"
              {...register('call_delay', { valueAsNumber: true })}
            />
            <p className="text-xs text-slate-400 mt-1">
              {t('manager.batchCalls.form.fields.callDelayHelp')}
            </p>
          </div>
          <div className="flex flex-col gap-2 pt-6">
            <Controller
              control={control}
              name="skip_if_no_appointment"
              render={({ field }) => (
                <div className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-900">
                  <Label htmlFor="skip-no-appt" className="cursor-pointer">
                    <div className="text-sm font-medium">
                      {t('manager.batchCalls.form.fields.skipNoAppointment')}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {t('manager.batchCalls.form.fields.skipNoAppointmentDesc')}
                    </div>
                  </Label>
                  <Switch
                    id="skip-no-appt"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </div>
              )}
            />
          </div>
        </div>

        <Controller
          control={control}
          name="retry_enabled"
          render={({ field }) => (
            <div className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-900">
              <Label htmlFor="retry-enabled" className="cursor-pointer">
                <div className="text-sm font-medium">
                  {t('manager.batchCalls.form.fields.retryEnabled')}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  {t('manager.batchCalls.form.fields.retryEnabledDesc')}
                </div>
              </Label>
              <Switch
                id="retry-enabled"
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </div>
          )}
        />

        {retryEnabled && (
          <div>
            <Label htmlFor="retry-at">
              {t('manager.batchCalls.form.fields.retryAt')}
            </Label>
            <Input
              id="retry-at"
              type="datetime-local"
              className="mt-1"
              {...register('retry_at')}
            />
          </div>
        )}
      </div>

      {/* 4. No-answer action */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 space-y-4">
        <h3 className="font-semibold text-slate-900 dark:text-white">
          {t('manager.batchCalls.form.sections.noAnswer')}
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() =>
              setValue('no_answer_action', 'none', { shouldDirty: true })
            }
            className={`p-3 border-2 rounded-lg text-left transition-all ${
              noAnswerAction === 'none'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10'
                : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
            }`}
          >
            <div className="font-medium text-sm text-slate-900 dark:text-white">
              {t('manager.batchCalls.form.noAnswer.none')}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {t('manager.batchCalls.form.noAnswer.noneDesc')}
            </div>
          </button>
          <button
            type="button"
            onClick={() =>
              setValue('no_answer_action', 'sms_client', { shouldDirty: true })
            }
            className={`p-3 border-2 rounded-lg text-left transition-all ${
              noAnswerAction === 'sms_client'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10'
                : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
            }`}
          >
            <div className="font-medium text-sm text-slate-900 dark:text-white">
              {t('manager.batchCalls.form.noAnswer.smsClient')}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {t('manager.batchCalls.form.noAnswer.smsClientDesc')}
            </div>
          </button>
        </div>

        {noAnswerAction === 'sms_client' && (
          <div>
            <Label htmlFor="sms-template">
              {t('manager.batchCalls.form.fields.smsTemplate')}
            </Label>
            <Textarea
              id="sms-template"
              rows={3}
              className="mt-1"
              {...register('no_answer_sms_template')}
            />
            <p className="text-xs text-slate-400 mt-1">
              {t('manager.batchCalls.form.fields.smsTemplateHelp')}
            </p>
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div className="flex justify-between pt-2">
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {t('manager.batchCalls.form.cancel')}
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {initialData
            ? t('manager.batchCalls.form.save')
            : t('manager.batchCalls.form.launch')}
        </Button>
      </div>

      {/* Note timezone */}
      <div className="flex items-start gap-2 text-xs text-slate-500 dark:text-slate-400">
        <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
        <span>{t('manager.batchCalls.form.timezoneNote')}</span>
      </div>
    </form>
  )
}
